import type { TFunction } from 'i18next';
import { useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useMatches } from 'react-router-dom';

import { focusPageHeadingOnArrival } from '../core-components/theme/components/Screen/saveBarFocus';
import type { RouteMatch } from '../globalComponents/BreadCrumbs';

const screenTitleOf = (matches: readonly RouteMatch[], t: TFunction): string | null => {
  const match = [...matches].reverse().find((candidate) => candidate.handle?.crumb !== undefined);
  const handle = match?.handle;
  if (match === undefined || handle === undefined) return null;
  const crumb = handle.crumb(match);
  return crumb.labelKey ? t(crumb.labelKey, crumb.labelFallback ?? crumb.label) : crumb.label;
};

export const documentTitleOf = (screen: string | null, app: string): string => {
  if (screen === null || screen === '') return app;
  return app === '' ? screen : `${screen} · ${app}`;
};

export const usePageArrival = (screenSlot: RefObject<HTMLElement>): void => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const matches = useMatches() as RouteMatch[];
  const [appTitle] = useState(() => document.title);
  const arrivedAt = useRef(pathname);
  const title = documentTitleOf(screenTitleOf(matches, t), appTitle);

  useEffect(() => {
    document.title = title;
    return () => {
      document.title = appTitle;
    };
  }, [title, appTitle]);

  useEffect(() => {
    if (arrivedAt.current === pathname || screenSlot.current === null) return undefined;
    arrivedAt.current = pathname;
    return focusPageHeadingOnArrival(screenSlot.current);
  }, [pathname, screenSlot]);
};
